/**
 * BAP Profile Display Utilities
 *
 * Combines display name resolution and profile merging logic
 * for consistent handling of BAP profiles across the application.
 */

import type { BAPIdentityData, BAPProfile, ResolvedIdentity } from "./core";

// ============================================================================
// PROFILE MERGING
// ============================================================================

/**
 * Safely merge profile changes without losing ANY existing data.
 *
 * Use this EVERYWHERE you update a profile to prevent data loss.
 *
 * @param bapProfile - The published on-chain profile (or null)
 * @param draftProfile - The current draft profile (or null)
 * @param changes - The fields you want to update
 * @returns A complete profile with all existing data preserved
 *
 * @example
 * const updated = mergeProfileChanges(bapProfile, draftProfile, {
 *   identity: { banner: newBannerUrl }
 * });
 */
export function mergeProfileChanges(
	bapProfile: Partial<BAPProfile> | null | undefined,
	draftProfile: Partial<BAPProfile> | null | undefined,
	changes: {
		identity?: Partial<BAPIdentityData>;
		[key: string]:
			| string
			| number
			| boolean
			| null
			| undefined
			| Partial<BAPIdentityData>;
	}
): Partial<BAPProfile> {
	return {
		// Start with published profile (all fields)
		...bapProfile,
		// Overlay draft changes (all fields)
		...draftProfile,
		// Apply new changes (all fields except identity)
		...changes,
		// Handle identity object specially - merge all three levels
		identity: {
			...bapProfile?.identity,
			...draftProfile?.identity,
			...changes.identity,
		} as BAPIdentityData,
	};
}

// ============================================================================
// PROFILE DISPLAY RESOLUTION
// ============================================================================

/**
 * Get merged profile data using priority order:
 * 1. profile (from KV draft > on-chain, already fetched by identity context)
 * 2. currentProfile (on-chain data for current identity)
 * 3. null
 *
 * This ensures all components use the same data source and priority.
 */
export function getMergedProfile(
	identity: ResolvedIdentity | null,
	currentProfile: BAPProfile | null = null
): BAPProfile | null {
	if (!identity) {
		return null;
	}

	// Use profile if the context resolved one (includes KV draft > on-chain);
	// when it resolved to nothing (null), still honor an explicitly passed
	// currentProfile rather than discarding it.
	return identity.profile ?? currentProfile;
}

/**
 * Resolve a human-chosen name from BAP identity data.
 *
 * THE single source of truth for name priority across client and server:
 * alternateName > givenName+familyName > legalName.
 *
 * Safe to import from server code - this module only imports types.
 * Backup labels (e.g. "Identity 1") are NOT part of this chain; callers
 * decide their own fallback when no profile name exists.
 */
export function resolveIdentityName(
	identity:
		| Partial<
				Pick<
					BAPIdentityData,
					"alternateName" | "givenName" | "familyName" | "legalName"
				>
		  >
		| null
		| undefined
): string | null {
	if (!identity) {
		return null;
	}
	const name =
		identity.alternateName?.trim() ||
		[identity.givenName, identity.familyName]
			.filter(Boolean)
			.join(" ")
			.trim() ||
		identity.legalName?.trim();
	return name || null;
}

/**
 * Get display name for an identity using consistent priority order:
 * 1. Cached profile (identity.profile) - KV draft > on-chain (already merged by context)
 * 2. Current profile (currentProfile) - on-chain data for current identity
 * 3. identity.displayName - populated by context from on-chain
 * 4. identity.name - from backup file
 * 5. Truncated idKey - last resort
 */
export function getDisplayName(
	currentProfile: BAPProfile | null,
	currentIdentity: ResolvedIdentity | null,
	fallback = "Account"
): string {
	if (!currentIdentity) {
		return fallback;
	}

	// 1. Profile name (merged KV draft > on-chain) via the canonical resolver
	const profile = getMergedProfile(currentIdentity, currentProfile);
	const profileName = resolveIdentityName(profile?.identity);
	if (profileName) {
		return profileName;
	}

	// 2. Check identity.displayName (from on-chain resolution cached in identity)
	// This is populated when profiles are resolved and contains alternateName
	if (
		currentIdentity.displayName &&
		currentIdentity.displayName !== currentIdentity.bapId
	) {
		return currentIdentity.displayName;
	}

	// 3. Use backup file name (stored in identity.name from BAP library)
	if (currentIdentity.name && currentIdentity.name !== currentIdentity.bapId) {
		return currentIdentity.name;
	}

	// 4. Fallback to truncated idKey
	return truncateId(currentIdentity.bapId);
}

/**
 * Truncate a BAP ID or address for compact display
 * Shows first 6 and last 6 characters with ellipsis
 */
export function truncateId(id: string, length = 12): string {
	if (id.length <= length) {
		return id;
	}
	const half = Math.floor(length / 2);
	return `${id.slice(0, half)}...${id.slice(-half)}`;
}
